import { useCallback } from 'react';
import {
  Measure,
  Validation,
  atLeastOne,
  mustBeEqual,
  mustBeGreaterOrEqual,
  mustBeLessOrEqual,
  NullableMeasure,
  InputFieldType,
} from '@e2m-customer-portal/shared';
import { useInputFieldErrorText } from '../use-input-field-error-text/use-input-field-error-text';
import { getLowerLimit, getUpperLimit } from './utils';
import { ForecastPageFieldLimit, ForecastPageLimitsResponse, LimitSpecification } from '../../generated/graphql';

export interface UseInputFieldRules {
  rules: Validation<NullableMeasure>[];
  minValue: NullableMeasure;
  maxValue: NullableMeasure;
}

export interface Limit {
  limit: Measure;
  limitSpecification: LimitSpecification;
  flag?: 'min' | 'max';
}

export interface Field {
  readonly type: InputFieldType;
  value: NullableMeasure;
  isComputed?: boolean;
}

const NULL_MEASURE = { value: null, unit: 'kW' };
const ZERO_MEASURE = { value: 0, unit: 'kW' } as const;

const validations = {
  isZero: mustBeEqual(ZERO_MEASURE),
};

function useLimitMapper(
  fields: readonly Field[],
  limits: ForecastPageLimitsResponse,
  minimum: NullableMeasure,
  maximum: NullableMeasure,
): (limit: ForecastPageFieldLimit) => [InputFieldType, UseInputFieldRules] {
  const { getMinErrorText, getMaxErrorText } = useInputFieldErrorText();

  return useCallback(
    (limit: ForecastPageFieldLimit) => {
      const minLimit = getLowerLimit(limit.type, limits, minimum);
      const maxLimit = getUpperLimit(limit.type, fields, limits, minimum, maximum);
      const allowZero = limit.type === InputFieldType.PRODUCTION;
      const rules: Validation<NullableMeasure>[] = [];

      if (maxLimit) {
        const msg = (maxLimit.flag === 'min' ? getMinErrorText : getMaxErrorText)(
          maxLimit.limit,
          limit.type,
          maxLimit.limitSpecification,
        );
        const check = mustBeLessOrEqual(maxLimit.limit, msg);
        rules.push(allowZero ? atLeastOne([validations.isZero, check], msg) : check);
      }

      if (minLimit) {
        const msg = (minLimit.flag === 'max' ? getMaxErrorText : getMinErrorText)(
          minLimit.limit,
          limit.type,
          minLimit.limitSpecification,
        );
        const check = mustBeGreaterOrEqual(minLimit.limit, msg);
        rules.push(allowZero ? atLeastOne([validations.isZero, check], msg) : check);
      }

      return [
        limit.type,
        {
          rules,
          minValue: minLimit?.limit ?? NULL_MEASURE,
          maxValue: maxLimit?.limit ?? NULL_MEASURE,
        },
      ];
    },
    [fields, limits, maximum, minimum, getMaxErrorText, getMinErrorText],
  );
}

export function useInputFieldRulesTyped(
  fields: readonly Field[],
  limits: ForecastPageLimitsResponse,
  minimum: NullableMeasure,
  maximum: NullableMeasure,
  type: InputFieldType,
): UseInputFieldRules {
  const map = useLimitMapper(fields, limits, minimum, maximum);
  const limit = limits.fields.find((f) => f.type === type);
  if (!limit) {
    return {
      rules: [],
      minValue: NULL_MEASURE,
      maxValue: NULL_MEASURE,
    };
  }

  return map(limit)[1];
}

export function useInputFieldRules(
  fields: readonly Field[],
  limits: ForecastPageLimitsResponse,
  minimum: NullableMeasure,
  maximum: NullableMeasure,
): Partial<Record<InputFieldType, UseInputFieldRules>> {
  const map = useLimitMapper(fields, limits, minimum, maximum);
  return Object.fromEntries(limits.fields.map(map));
}
